import { uuid } from "@core/foundation/id";
import { omitUndefined } from "@core/foundation/object";
import { hasValue, isNullish } from "@core/foundation/types";
import type {
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonRepositoryHelperContract,
  PK,
  SK,
} from "@core/ports/json-storage";
import {
  JsonDuplicateError,
  JsonWriteConflictError,
} from "@core/ports/json-storage";
import type { ZodObject, ZodReadonly, ZodType } from "@core/zod";
import { z } from "@core/zod";

import type { IndexedDbClient, IndexedDbOperation } from "./IndexedDbClient.ts";
import type { IndexedDbTransaction } from "./IndexedDbTransaction.ts";

interface Row {
  readonly PK: string;
  readonly SK: string;
  readonly data: unknown;
}

const EtagSchema = z.string();

export class IndexedDbRepositoryHelper<
  Identifier extends ZodObject,
  Data extends ZodObject,
  EntityType extends string,
> implements
    JsonRepositoryHelperContract<
      Identifier,
      Data,
      IndexedDbTransaction,
      EntityType
    >
{
  public readonly __typeTag = "JsonRepositoryHelperContract";

  public readonly entityType: EntityType;

  private readonly etagField?: keyof z.infer<ZodReadonly<Data>>;

  private readonly indexedDb: IndexedDbClient;

  private readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;

  private readonly tableName: string;

  public constructor({
    entityType,
    etagField,
    indexedDb,
    readSchema,
    tableName,
  }: {
    readonly entityType: EntityType;
    readonly etagField?: keyof z.infer<ZodReadonly<Data>>;
    readonly indexedDb: IndexedDbClient;
    readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;
    readonly tableName: string;
  }) {
    this.entityType = entityType;
    this.etagField = etagField;
    this.indexedDb = indexedDb;
    this.readSchema = readSchema;
    this.tableName = tableName;
  }

  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  public async create(
    PK: PK<EntityType>,
    SK: SK,
    payload: JsonPayload<Identifier, Data>,
  ): Promise<JsonPayload<Identifier, Data>> {
    const { operation, withEtag } = this.buildAdd(PK, SK, payload);

    const { committed } = await this.indexedDb.transact([operation]);

    if (!committed) {
      throw new JsonDuplicateError(`Duplicate PK=${PK} SK=${SK}`);
    }

    return withEtag;
  }

  public async getById(
    PK: PK<EntityType>,
    SK: SK,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const store = await this.getStore("readonly");
    const row: Row | undefined = await this.getRequestResult(
      store.get([PK, SK]),
    );

    if (isNullish(row)) {
      return null;
    }

    return this.convertRow(row);
  }

  public async getByIds(
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ): Promise<readonly JsonPayload<Identifier, Data>[]> {
    if (keys.length === 0) {
      return [];
    }

    const store = await this.getStore("readonly");
    const rows = await Promise.all(
      keys.map(async key => {
        const row: Row | undefined = await this.getRequestResult(
          store.get([key.PK, key.SK]),
        );

        return row;
      }),
    );

    return this.convertRows(rows.filter(hasValue));
  }

  public async getByPK(
    PK: PK<EntityType>,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.list({ PK, lower: [PK], options, upper: [PK, []] });
  }

  public async getBySKGt(
    PK: PK<EntityType>,
    SK: SK,
    options: Omit<JsonPaginationOptions, "order"> = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.list({
      PK,
      lower: [PK, SK],
      lowerOpen: true,
      options: { ...options, order: "asc" },
      upper: [PK, []],
    });
  }

  public async getBySKLt(
    PK: PK<EntityType>,
    SK: SK,
    options: Omit<JsonPaginationOptions, "order"> = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.list({
      PK,
      lower: [PK],
      options: { ...options, order: "desc" },
      upper: [PK, SK],
      upperOpen: true,
    });
  }

  public async getBySKPrefix(
    PK: PK<EntityType>,
    SKPrefix: SK,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    const MAX_CODE_POINT = 0xff_ff;

    return this.list({
      PK,
      lower: [PK, SKPrefix],
      options,
      upper: [PK, `${SKPrefix}${String.fromCodePoint(MAX_CODE_POINT)}`],
    });
  }

  public prepareCreate(
    transaction: IndexedDbTransaction,
    PK: PK<EntityType>,
    SK: SK,
    payload: JsonPayload<Identifier, Data>,
  ): void {
    const { operation } = this.buildAdd(PK, SK, payload);
    transaction.add(operation);
  }

  public prepareUpdateById(
    transaction: IndexedDbTransaction,
    PK: PK<EntityType>,
    SK: SK,
    data: Partial<z.infer<ZodReadonly<Data>>>,
  ): void {
    transaction.add(this.buildMerge(PK, SK, data));
  }

  /**
   * @throws {JsonWriteConflictError} If etag provided and doesn't match.
   */
  public async updateById(
    PK: PK<EntityType>,
    SK: SK,
    data: Partial<z.infer<ZodReadonly<Data>>>,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const { committed } = await this.indexedDb.transact([
      this.buildMerge(PK, SK, data),
    ]);

    if (!committed) {
      return null;
    }

    return this.getById(PK, SK);
  }

  private buildAdd(
    PK: PK<EntityType>,
    SK: SK,
    payload: JsonPayload<Identifier, Data>,
  ): {
    readonly operation: IndexedDbOperation;
    readonly withEtag: JsonPayload<Identifier, Data>;
  } {
    const withEtag = hasValue(this.etagField)
      ? { ...payload, [this.etagField]: uuid() }
      : payload;

    return {
      operation: {
        run: (store, fail) => {
          const request = store.add({ PK, SK, data: withEtag });

          request.addEventListener("error", event => {
            if (request.error?.name === "ConstraintError") {
              event.preventDefault();
              fail();
            }
          });
        },
        storeName: this.tableName,
      },
      withEtag,
    };
  }

  private buildMerge(
    PK: PK<EntityType>,
    SK: SK,
    data: Partial<z.infer<ZodReadonly<Data>>>,
  ): IndexedDbOperation {
    const update = omitUndefined(data);

    const etag =
      hasValue(this.etagField) && this.etagField in update
        ? {
            expected: EtagSchema.parse(update[this.etagField]),
            field: this.etagField,
            next: uuid(),
          }
        : null;

    return {
      run: (store, fail) => {
        const request: IDBRequest<Row | undefined> = store.get([PK, SK]);

        request.addEventListener("success", () => {
          const row = request.result;

          if (isNullish(row)) {
            fail();

            return;
          }

          const stored = this.convertRow(row);

          if (hasValue(etag) && stored[etag.field] !== etag.expected) {
            fail(new JsonWriteConflictError(`Etag conflict PK=${PK} SK=${SK}`));

            return;
          }

          const merged = hasValue(etag)
            ? { ...stored, ...update, [etag.field]: etag.next }
            : { ...stored, ...update };

          store.put({ PK, SK, data: merged });
        });
      },
      storeName: this.tableName,
    };
  }

  private buildPaginatedResult(
    rows: readonly Row[],
    limit?: number,
  ): JsonPaginatedResult<Identifier, Data> {
    if (isNullish(limit)) {
      return { items: this.convertRows(rows) };
    }

    const excessRow = rows[limit];

    if (!excessRow) {
      return { items: this.convertRows(rows) };
    }

    return {
      items: this.convertRows(rows.slice(0, limit)),
      nextCursor: excessRow.SK,
    };
  }

  private convertRow(row: Row): JsonPayload<Identifier, Data> {
    return this.readSchema.parse(row.data);
  }

  private convertRows(
    rows: readonly Row[],
  ): readonly JsonPayload<Identifier, Data>[] {
    return rows.map(row => this.convertRow(row));
  }

  private async getCursorRows({
    limit,
    order,
    range,
    store,
  }: {
    readonly limit: number | undefined;
    readonly order: "asc" | "desc";
    readonly range: IDBKeyRange;
    readonly store: IDBObjectStore;
  }): Promise<readonly Row[]> {
    return new Promise((resolve, reject) => {
      // @mutable
      const rows: Row[] = [];

      const idbDirectionMap = { asc: "next", desc: "prev" } as const;
      const idbDirection = idbDirectionMap[order];
      const request = store.openCursor(range, idbDirection);

      request.addEventListener("success", () => {
        const cursor = request.result;

        if (!cursor) {
          resolve(rows);

          return;
        }

        const row: Row = cursor.value;
        rows.push(row);

        if (hasValue(limit) && rows.length >= limit + 1) {
          resolve(rows);

          return;
        }

        cursor.continue();
      });

      request.addEventListener("error", () => {
        reject(
          request.error ?? new Error("Failed to read an IndexedDB cursor."),
        );
      });
    });
  }

  private async getRequestResult<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.addEventListener("success", () => {
        resolve(request.result);
      });

      request.addEventListener("error", () => {
        reject(request.error ?? new Error("IndexedDB request failed."));
      });
    });
  }

  private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.indexedDb.connect();

    return db.transaction(this.tableName, mode).objectStore(this.tableName);
  }

  private async list({
    PK,
    lower,
    lowerOpen = false,
    options: { cursor, limit, order = "asc" },
    upper,
    upperOpen = false,
  }: {
    readonly PK: PK<EntityType>;
    readonly lower: readonly unknown[];
    readonly lowerOpen?: boolean;
    readonly options: JsonPaginationOptions;
    readonly upper: readonly unknown[];
    readonly upperOpen?: boolean;
  }): Promise<JsonPaginatedResult<Identifier, Data>> {
    let range: IDBKeyRange;

    if (isNullish(cursor)) {
      range = IDBKeyRange.bound(lower, upper, lowerOpen, upperOpen);
    } else if (order === "asc") {
      range = IDBKeyRange.bound([PK, cursor], upper, true, upperOpen);
    } else {
      range = IDBKeyRange.bound(lower, [PK, cursor], lowerOpen, true);
    }

    const store = await this.getStore("readonly");
    const rows = await this.getCursorRows({ limit, order, range, store });

    return this.buildPaginatedResult(rows, limit);
  }
}
